import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '../../store/authStore'
import { api } from '../../lib/api'
import { translateApiError } from '../../lib/apiErrors'
import {
  validateOAuthAuthorizeQuery,
  type OAuthAuthorizeParamsError,
} from '../../lib/oauth/validateAuthorizeRequest'
import AuthShell from '../../components/auth/AuthShell'
import AuthBrand from '../../components/auth/AuthBrand'
import { authHeadingClass, authSubtextClass, authSubmitClass } from '../../components/auth/authTheme'

const PARAM_ERROR_KEYS: Record<OAuthAuthorizeParamsError, string> = {
  missingParams: 'auth:oauth.authorize.errors.missingParams',
  unsupportedResponseType: 'auth:oauth.authorize.errors.unsupportedResponseType',
}

/**
 * ClickUp-style portal SSO hop.
 * Quackback redirects here → if logged into Ofertator, mint auth code → bounce back.
 */
export default function OAuthAuthorize() {
  const { t } = useTranslation(['auth', 'common', 'errors'])
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const { isAuthenticated, isSessionReady, token, user, getCurrentUser } = useAuthStore()
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(true)

  const clientId = params.get('client_id') || ''
  const redirectUri = params.get('redirect_uri') || ''
  const responseType = params.get('response_type') || 'code'
  const scope = params.get('scope') || 'openid email profile'
  const state = params.get('state') || undefined

  useEffect(() => {
    if (token && !user) {
      void getCurrentUser()
    }
  }, [token, user, getCurrentUser])

  useEffect(() => {
    if (!isSessionReady) return

    const paramError = validateOAuthAuthorizeQuery({ clientId, redirectUri, responseType })
    if (paramError) {
      setError(t(PARAM_ERROR_KEYS[paramError]))
      setBusy(false)
      return
    }

    if (!isAuthenticated || !token) {
      navigate('/auth/login', {
        replace: true,
        state: {
          from: {
            pathname: '/oauth/authorize',
            search: `?${params.toString()}`,
          },
        },
      })
      return
    }

    if (!user) return

    let cancelled = false
    ;(async () => {
      try {
        const { data } = await api.post<{ redirect_to: string }>('/oauth/authorize/code', {
          client_id: clientId,
          redirect_uri: redirectUri,
          response_type: responseType,
          scope,
          state,
        })
        if (!cancelled) {
          window.location.replace(data.redirect_to)
        }
      } catch (e: unknown) {
        if (!cancelled) {
          setError(translateApiError(e, t) || t('auth:oauth.authorize.errors.failed'))
          setBusy(false)
        }
      }
    })()

    return () => {
      cancelled = true
    }
  }, [
    clientId,
    redirectUri,
    responseType,
    scope,
    state,
    isSessionReady,
    isAuthenticated,
    token,
    user,
    navigate,
    params,
    t,
  ])

  return (
    <AuthShell>
      <AuthBrand />
      <h1 className={authHeadingClass}>{t('auth:oauth.authorize.heading')}</h1>
      {busy && !error && (
        <p className={authSubtextClass}>{t('auth:oauth.authorize.subtext')}</p>
      )}
      {error && (
        <div className="mt-4 space-y-3">
          <p className="text-sm text-red-600">{error}</p>
          <Link to="/dashboard" className={authSubmitClass}>
            {t('auth:oauth.authorize.backToDashboard')}
          </Link>
        </div>
      )}
    </AuthShell>
  )
}
